import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminOrders,
  setOrderStatus,
  createAdminOrder,
  editAdminOrder,
  deleteAdminOrder,
  generateInvoiceFromOrder,
  type CreateOrderInput,
} from "@/services/adminService";
import { sendErpEmail } from "@/services/erpEmailService";
import type { ErpOrder, ErpOrderStatus } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard, adminTh, adminTd, adminIconBtn, adminBtnGhost, adminBtnPrimary, adminBtnDanger, adminInput, adminLabel,
} from "@/pages/portal/admin/config/ui";
import { downloadCsv } from "@/utils/csv";

const STATUSES: ErpOrderStatus[] = ["pending", "paid", "processing", "fulfilled", "refunded", "cancelled"];
const CREATE_STATUSES: ErpOrderStatus[] = ["pending", "paid", "processing", "fulfilled"];
const PAYMENT_METHODS = ["Manual entry", "Bank transfer", "UPI", "Cash", "Card", "Cheque"];

const STATUS_BADGE: Record<ErpOrderStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-secondary-100 text-secondary-800",
  processing: "bg-primary-100 text-primary-700",
  fulfilled: "bg-emerald-100 text-emerald-800",
  refunded: "bg-rose-100 text-rose-700",
  cancelled: "bg-background-200 text-foreground-600",
};

const fmt = (n: number) => n.toLocaleString("en-IN");
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const EMPTY_ORDER: CreateOrderInput = {
  customer: "", customerEmail: "", business: "", plan: "", amount: 0,
  currency: "INR", paymentMethod: "Manual entry", status: "pending",
};

type Notice = { kind: "success" | "error"; text: string } | null;

export default function OrdersSection() {
  const { accessToken } = useAuth();
  const { refresh: refreshCounters } = useAdminCounters();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminOrders(accessToken),
    [] as ErpOrder[],
    [accessToken],
  );
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | ErpOrderStatus>("all");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  // create / edit form
  const [form, setForm] = useState<CreateOrderInput | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<ErpOrder | null>(null);

  const stats = useMemo(() => {
    const collected = data.filter((o) => o.status === "paid" || o.status === "fulfilled").reduce((s, o) => s + o.amount, 0);
    const pending = data.filter((o) => o.status === "pending" || o.status === "processing").length;
    const refunded = data.filter((o) => o.status === "refunded").reduce((s, o) => s + o.amount, 0);
    return { collected, pending, refunded, total: data.length };
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((o) => {
      const matchStatus = status === "all" || o.status === status;
      const matchQ = !q || [o.orderNo, o.customer, o.business, o.plan].some((v) => v.toLowerCase().includes(q));
      return matchStatus && matchQ;
    });
  }, [data, search, status]);

  async function apply(order: ErpOrder, next: ErpOrderStatus) {
    setBusyId(order.id);
    setData((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: next } : o)));
    try {
      await setOrderStatus(accessToken, order.id, next);
      refreshCounters();
    } catch {
      setData((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: order.status } : o)));
    } finally {
      setBusyId(null);
    }
  }

  function openCreate() {
    setFormError("");
    setEditId(null);
    setForm({ ...EMPTY_ORDER });
  }

  function openEdit(order: ErpOrder) {
    setFormError("");
    setEditId(order.id);
    setForm({
      customer: order.customer,
      customerEmail: order.customerEmail ?? "",
      business: order.business,
      plan: order.plan,
      amount: order.amount,
      currency: order.currency,
      paymentMethod: order.paymentMethod,
      status: order.status,
    });
  }

  function closeForm() {
    setForm(null);
    setEditId(null);
    setFormError("");
  }

  const set = <K extends keyof CreateOrderInput>(key: K, value: CreateOrderInput[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  async function submit() {
    if (!form?.customer?.trim()) return;
    setSaving(true);
    setFormError("");
    try {
      if (editId) {
        const updated = await editAdminOrder(accessToken, editId, form);
        setData((prev) => prev.map((o) => (o.id === editId ? updated : o)));
        setNotice({ kind: "success", text: `Order ${updated.orderNo} updated.` });
      } else {
        const created = await createAdminOrder(accessToken, form);
        setData((prev) => [created, ...prev]);
        setNotice({ kind: "success", text: `Order ${created.orderNo} created.` });
      }
      closeForm();
      refreshCounters();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not save the order.");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteAdminOrder(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      setNotice({ kind: "success", text: `Order ${deleteTarget.orderNo} deleted.` });
      setDeleteTarget(null);
      refreshCounters();
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : "Could not delete the order." });
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  async function generateInvoice(order: ErpOrder) {
    setBusyId(order.id);
    setNotice(null);
    try {
      const invoice = await generateInvoiceFromOrder(accessToken, order);
      setData((prev) => prev.map((o) => (o.id === order.id ? { ...o, invoiceId: invoice.id, invoiceNo: invoice.invoiceNo } : o)));
      setNotice({ kind: "success", text: `Invoice ${invoice.invoiceNo} generated from ${order.orderNo} and linked.` });
      refreshCounters();
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : "Could not generate the invoice." });
    } finally {
      setBusyId(null);
    }
  }

  async function sendEmail(order: ErpOrder) {
    setBusyId(order.id);
    setNotice(null);
    try {
      const msg = await sendErpEmail(accessToken, {
        type: "order",
        to: order.customerEmail ?? "",
        reference: order.orderNo,
        customer: order.customer,
        business: order.business,
        plan: order.plan,
        amount: order.amount,
        currency: order.currency,
        status: order.status,
        issuedAt: order.createdAt,
      });
      setNotice({ kind: "success", text: msg });
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : "Could not send the email." });
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    downloadCsv(
      `erp-orders-${new Date().toISOString().slice(0, 10)}`,
      ["Order No", "Customer", "Email", "Business", "Plan", "Amount", "Currency", "Payment", "Status", "Invoice", "Date"],
      filtered.map((o) => [o.orderNo, o.customer, o.customerEmail ?? "", o.business, o.plan, o.amount, o.currency, o.paymentMethod, o.status, o.invoiceNo ?? "", fmtDate(o.createdAt)]),
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Total Orders" value={loading ? "—" : fmt(stats.total)} icon="ri-shopping-bag-3-line" accent="primary" />
        <AdminStatCard label="Revenue Collected" value={loading ? "—" : `₹${fmt(stats.collected)}`} icon="ri-money-rupee-circle-line" accent="emerald" />
        <AdminStatCard label="Awaiting Payment" value={loading ? "—" : fmt(stats.pending)} icon="ri-time-line" accent="amber" sub="pending / processing" />
        <AdminStatCard label="Refunded" value={loading ? "—" : `₹${fmt(stats.refunded)}`} icon="ri-arrow-go-back-line" accent="rose" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search order no, customer, business…" />
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "all" | ErpOrderStatus)}
            className="h-10 w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 outline-none focus:border-primary-400 sm:w-48"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
            className={`${adminBtnGhost} whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <i className="ri-download-2-line text-base" /> Export CSV
          </button>
          <button type="button" onClick={openCreate} className={`${adminBtnPrimary} whitespace-nowrap`}>
            <i className="ri-add-line text-base" /> Add order
          </button>
        </div>
      </div>

      {notice && (
        <div
          className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 text-sm ${
            notice.kind === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
          }`}
        >
          <span className="flex items-start gap-2.5">
            <i className={`${notice.kind === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} mt-0.5 text-base`} />
            {notice.text}
          </span>
          <button type="button" onClick={() => setNotice(null)} className="cursor-pointer text-current opacity-70 hover:opacity-100">
            <i className="ri-close-line text-base" />
          </button>
        </div>
      )}

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1020px] border-collapse">
            <thead className="border-b border-background-200 bg-background-100/60">
              <tr>
                <th className={adminTh}>Order</th>
                <th className={adminTh}>Customer</th>
                <th className={adminTh}>Business / Plan</th>
                <th className={adminTh}>Amount</th>
                <th className={adminTh}>Status</th>
                <th className={adminTh}>Invoice</th>
                <th className={adminTh}>Date</th>
                <th className={`${adminTh} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading ? (
                <tr><td colSpan={8} className="py-14 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-14 text-center text-sm text-foreground-500">No orders match your filters.</td></tr>
              ) : (
                filtered.map((o) => (
                  <tr key={o.id} className="hover:bg-background-100/50">
                    <td className={`${adminTd} font-semibold text-foreground-900`}>{o.orderNo}</td>
                    <td className={adminTd}>
                      <p className="text-foreground-800">{o.customer}</p>
                      {o.customerEmail && <p className="text-xs text-foreground-500">{o.customerEmail}</p>}
                    </td>
                    <td className={adminTd}>
                      <p className="text-foreground-800">{o.business}</p>
                      <p className="text-xs text-foreground-500">{o.plan} · {o.paymentMethod}</p>
                    </td>
                    <td className={`${adminTd} tabular-nums font-semibold text-foreground-900`}>{o.amount > 0 ? `₹${fmt(o.amount)}` : "Free"}</td>
                    <td className={adminTd}>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${STATUS_BADGE[o.status]}`}>{o.status}</span>
                    </td>
                    <td className={adminTd}>
                      {o.invoiceNo ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
                          <i className="ri-link" /> {o.invoiceNo}
                        </span>
                      ) : (
                        <span className="text-xs text-foreground-400">Not invoiced</span>
                      )}
                    </td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{fmtDate(o.createdAt)}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex flex-wrap justify-end gap-1">
                        {o.status === "pending" && (
                          <button type="button" disabled={busyId === o.id} onClick={() => apply(o, "paid")} className={adminIconBtn} title="Mark as paid"><i className="ri-money-dollar-circle-line text-base text-emerald-600" /></button>
                        )}
                        {(o.status === "paid" || o.status === "processing") && (
                          <button type="button" disabled={busyId === o.id} onClick={() => apply(o, "fulfilled")} className={adminIconBtn} title="Mark fulfilled"><i className="ri-checkbox-circle-line text-base text-emerald-600" /></button>
                        )}
                        {(o.status === "paid" || o.status === "fulfilled") && (
                          <button type="button" disabled={busyId === o.id} onClick={() => apply(o, "refunded")} className={adminIconBtn} title="Refund order"><i className="ri-arrow-go-back-line text-base text-rose-600" /></button>
                        )}
                        {o.status === "pending" && (
                          <button type="button" disabled={busyId === o.id} onClick={() => apply(o, "cancelled")} className={adminIconBtn} title="Cancel order"><i className="ri-close-circle-line text-base text-rose-600" /></button>
                        )}
                        {!o.invoiceNo && (
                          <button type="button" disabled={busyId === o.id} onClick={() => generateInvoice(o)} className={adminIconBtn} title="Generate invoice"><i className="ri-file-add-line text-base text-secondary-600" /></button>
                        )}
                        <button
                          type="button"
                          disabled={busyId === o.id || !o.customerEmail}
                          onClick={() => sendEmail(o)}
                          className={`${adminIconBtn} disabled:cursor-not-allowed disabled:opacity-40`}
                          title={o.customerEmail ? "Email order confirmation" : "Add a customer email first"}
                        >
                          <i className="ri-mail-send-line text-base text-primary-600" />
                        </button>
                        <button type="button" disabled={busyId === o.id} onClick={() => openEdit(o)} className={adminIconBtn} title="Edit order"><i className="ri-pencil-line text-base" /></button>
                        <button type="button" disabled={busyId === o.id} onClick={() => setDeleteTarget(o)} className={adminIconBtn} title="Delete order"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form !== null && (
        <AdminModal
          onClose={closeForm}
          title={editId ? "Edit plan order" : "Add plan order"}
          subtitle={editId ? "Update the details of this listing-plan order." : "Record a listing-plan order taken over the phone, email or in person."}
          footer={
            <>
              <button type="button" onClick={closeForm} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!form?.customer?.trim() || saving} onClick={submit} className={adminBtnPrimary}>
                {saving ? "Saving…" : editId ? "Save changes" : "Create order"}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={adminLabel} htmlFor="or-customer">Customer name</label>
              <input id="or-customer" type="text" value={form.customer} onChange={(e) => set("customer", e.target.value)} className={adminInput} placeholder="e.g. Ramesh Kumar" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-email">Customer email</label>
              <input id="or-email" type="email" value={form.customerEmail ?? ""} onChange={(e) => set("customerEmail", e.target.value)} className={adminInput} placeholder="optional — links to their portal orders" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-business">Business</label>
              <input id="or-business" type="text" value={form.business ?? ""} onChange={(e) => set("business", e.target.value)} className={adminInput} placeholder="e.g. Sunrise Bakery" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-plan">Plan</label>
              <input id="or-plan" type="text" value={form.plan ?? ""} onChange={(e) => set("plan", e.target.value)} className={adminInput} placeholder="e.g. Premium Yearly" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-amount">Amount (₹)</label>
              <input id="or-amount" type="number" min={0} value={form.amount ?? 0} onChange={(e) => set("amount", Number(e.target.value) || 0)} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-payment">Payment method</label>
              <select id="or-payment" value={form.paymentMethod ?? "Manual entry"} onChange={(e) => set("paymentMethod", e.target.value)} className={`${adminInput} cursor-pointer`}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="or-status">Status</label>
              <select id="or-status" value={form.status ?? "pending"} onChange={(e) => set("status", e.target.value as ErpOrderStatus)} className={`${adminInput} cursor-pointer`}>
                {(editId ? STATUSES : CREATE_STATUSES).map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>
          {formError && (
            <p className="mt-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <i className="ri-error-warning-line text-base" /> {formError}
            </p>
          )}
        </AdminModal>
      )}

      {deleteTarget !== null && (
        <AdminModal
          onClose={() => setDeleteTarget(null)}
          title="Delete order"
          footer={
            <>
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                {busyId === deleteTarget.id ? "Deleting…" : "Delete order"}
              </button>
            </>
          }
        >
          <p className="text-sm text-foreground-700">
            Permanently delete <strong>{deleteTarget.orderNo}</strong> ({deleteTarget.customer})? This cannot be undone.
            {deleteTarget.invoiceNo && (
              <span className="mt-2 block text-xs text-foreground-500">
                Note: linked invoice {deleteTarget.invoiceNo} will keep its record but lose the order link.
              </span>
            )}
          </p>
        </AdminModal>
      )}
    </div>
  );
}